import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from './Icon';

export interface EmptyStateProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Buttons under the text. */
  readonly actions?: ReactNode;
  /** Icon in the 40px tile (card variant only). */
  readonly icon?: LucideIcon;
  /**
   * `card`: empty period / empty screen (radius 16, 56px padding, icon tile, 18px title).
   * `compact`: "no rows match the filters" (radius 10, 40px padding, 15px title).
   */
  readonly variant?: 'card' | 'compact';
  readonly className?: string;
}

/** Empty and no-match states of the list screens. */
export function EmptyState({
  title,
  description,
  actions,
  icon = Inbox,
  variant = 'card',
  className,
}: EmptyStateProps) {
  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-1.5 rounded-2 border border-border-1 bg-bg-3 px-6 py-10 text-center',
          className,
        )}
      >
        <div className="text-[15px] font-semibold">{title}</div>
        {description !== undefined && <div className="text-fg-2">{description}</div>}
        {actions !== undefined && <div className="mt-2 flex gap-2">{actions}</div>}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-3 border border-border-1 bg-bg-3 px-6 py-14 text-center',
        className,
      )}
    >
      <div className="grid size-10 place-items-center rounded-2 bg-bg-2 text-fg-2">
        <Icon icon={icon} size={20} />
      </div>
      <div className="mt-1.5 text-[18px] font-semibold">{title}</div>
      {description !== undefined && (
        <div className="max-w-[440px] text-[14px] leading-[1.55] text-fg-2">{description}</div>
      )}
      {actions !== undefined && <div className="mt-3 flex gap-2">{actions}</div>}
    </div>
  );
}
